import { useQueries } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import type { SynchronousQueryConfig } from 'lib/react-query';
import type { AppError } from 'lib/api';
import queryString from 'query-string';
import { apiClient } from 'lib/api';
import { parseCreativeIdDigests } from 'hooks';
import queryKeys from '../api/queryKeys';
import type { CreativeEntry } from 'types/shared';

const FACEBOOK_NETWORK = 'Facebook';

export type UploadDate = 'last_week' | 'last_2_weeks' | '1_month' | 'all';

export interface CreativeParams {
  query?: string[] | string;
  ids?: string[];
  app?: string;
  ad_type?: string;
  network?: string;
  locale?: string;
  hypothesis?: string[];
  customer?: string;
  flow?: string[];
  technology?: string[];
  status?: string;
  search?: string;
  upload_date?: UploadDate;
  tpc_finish_date?: string;
}

const hasCreativeQueryFilter = (
  query: CreativeParams['query'],
): query is string[] | string =>
  Array.isArray(query) ? query.length > 0 : typeof query === 'string' && query.length > 0;

/** Drops empty `query` / `ids` so browse and deep-link requests share one cache key. */
export const normalizeCreativeParams = (
  params: CreativeParams,
): CreativeParams => {
  let normalized = params;

  if (!hasCreativeQueryFilter(normalized.query)) {
    const rest = { ...normalized };
    delete rest.query;
    normalized = rest;
  }

  if (!normalized.ids?.length) {
    const rest = { ...normalized };
    delete rest.ids;
    normalized = rest;
  }

  return normalized;
};

export const getCreatives = async (
  { params }: { params: CreativeParams },
  signal?: AbortSignal,
): Promise<CreativeEntry[]> => {
  const normalizedParams = normalizeCreativeParams(params);
  const res = await apiClient.get<CreativeEntry[]>(
    (queryKeys.allCreatives as string[]).join('/'),
    {
      params: normalizedParams,
      signal,
      paramsSerializer: (prms) =>
        queryString.stringify(prms, { arrayFormat: 'comma' }),
    },
  );
  return res.data;
};

export const dedupeCreativesById = (
  items: readonly CreativeEntry[],
): CreativeEntry[] => {
  const byId = new Map<string, CreativeEntry>();
  for (const item of items) {
    if (item?.id) {
      byId.set(item.id, item);
    }
  }
  return [...byId.values()];
};

/**
 * Fetches creatives for Facebook deep-link initialization when the URL already
 * contains creative ids. Loads those ids via `ids` and the default browse list
 * in parallel, then merges them (deduped by id) so the base query cache includes
 * both selected and browsable creatives.
 *
 * Non-Facebook networks fall back to a single `get_creatives` request.
 */
export const fetchCreativesWithUrlIds = async (
  {
    params,
    urlCreativeIds,
  }: { params: CreativeParams; urlCreativeIds: readonly string[] },
  signal?: AbortSignal,
): Promise<CreativeEntry[]> => {
  const normalizedParams = normalizeCreativeParams(params);
  const isFacebookRequest = normalizedParams.network === FACEBOOK_NETWORK;
  const uniqueUrlIds = [...new Set(urlCreativeIds)];
  const urlIdDigests = parseCreativeIdDigests(uniqueUrlIds);

  if (!isFacebookRequest || urlIdDigests.length === 0) {
    return getCreatives({ params: normalizedParams }, signal);
  }

  const [matchedByIds, baseList] = await Promise.all([
    getCreatives(
      { params: { ...normalizedParams, ids: urlIdDigests } },
      signal,
    ),
    getCreatives({ params: normalizedParams }, signal),
  ]);

  return dedupeCreativesById([...baseList, ...matchedByIds]);
};

/** Splits the `query` filter into the individual values the user picked. */
export const toCreativeQueryValues = (
  query: CreativeParams['query'],
): string[] => {
  const rawValues = Array.isArray(query)
    ? query
    : typeof query === 'string'
      ? query.split(',')
      : [];

  return [...new Set(rawValues.map((value) => value.trim()).filter(Boolean))];
};

interface CombinedCreativesResult {
  data?: CreativeEntry[];
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: AppError | null;
}

/**
 * Flattens the per-query-value responses into one deduped list. `isLoading`
 * stays false while at least one value already has data, so adding another
 * value to the filter extends the current list instead of blanking it; the
 * merged list is only complete (`isSuccess`) once every value responded.
 */
export const combineCreativesResults = (
  results: UseQueryResult<CreativeEntry[], AppError>[],
): CombinedCreativesResult => {
  const loaded = results.filter(
    (result): result is UseQueryResult<CreativeEntry[], AppError> & { data: CreativeEntry[] } =>
      !!result.data,
  );
  const data = loaded.length
    ? dedupeCreativesById(loaded.flatMap((result) => result.data))
    : undefined;

  return {
    data,
    isLoading: !data && results.some((result) => result.isLoading),
    isFetching: results.some((result) => result.isFetching),
    isSuccess: results.every((result) => result.isSuccess),
    isError: results.some((result) => result.isError),
    error: results.find((result) => result.error)?.error ?? null,
  };
};

interface UseCreativesOptions {
  params: CreativeParams;
  config?: SynchronousQueryConfig<typeof getCreatives, CreativeEntry[]>;
}

/**
 * The backend matches `query` as a single name, so a multi-value name filter is
 * fanned out into one request per value, plus the unfiltered browse request,
 * and merged back into one list (matches first, browse list last). Each value
 * keeps its own cache entry, which is what makes adding values one by one cheap
 * (only the new value is fetched) and additive.
 */
export const useCreatives = ({ params, config }: UseCreativesOptions) => {
  const normalizedParams = normalizeCreativeParams(params);
  const queryValues = toCreativeQueryValues(normalizedParams.query);
  const hasNameFilter = queryValues.length > 0;
  const isFacebookNetwork = normalizedParams.network === FACEBOOK_NETWORK;

  const baseParams = normalizeCreativeParams({
    ...normalizedParams,
    query: undefined,
  });
  const paramsPerQueryValue = hasNameFilter
    ? [
        ...queryValues.map((value) =>
          normalizeCreativeParams({ ...normalizedParams, query: [value] }),
        ),
        baseParams,
      ]
    : [baseParams];

  return useQueries({
    queries: paramsPerQueryValue.map((valueParams) => ({
      staleTime: 0,
      refetchOnMount: isFacebookNetwork ? hasNameFilter : true,
      ...config,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        getCreatives({ params: valueParams }, signal),
      queryKey: queryKeys.creatives<CreativeParams>(valueParams),
    })),
    combine: combineCreativesResults,
  });
};
